import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Star, Loader2, CheckCircle, AlertCircle, Quote } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const ReviewSubmissionPage = () => {
  const { orderId } = useParams();
  const [loading, setLoading] = useState(true);
  const [eligibility, setEligibility] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', occasion: '', rating: 5, text: '' });

  useEffect(() => {
    if (!orderId) return;
    axios
      .get(`${API}/api/review/${orderId}`)
      .then((res) => {
        setEligibility(res.data);
        setForm((f) => ({
          ...f,
          name: res.data.name || '',
          occasion: res.data.occasion || '',
        }));
        if (res.data.already_submitted) setSubmitted(true);
      })
      .catch((err) => {
        setEligibility({ eligible: false, error: err.response?.data?.detail || 'Order not found' });
      })
      .finally(() => setLoading(false));
  }, [orderId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) {
      toast.error('Please fill in your name and review');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/review/${orderId}`, {
        name: form.name.trim(),
        occasion: form.occasion.trim(),
        rating: form.rating,
        text: form.text.trim(),
      });
      setSubmitted(true);
      toast.success('Thank you so much for your review!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-amber-500 animate-spin mx-auto mb-4" />
          <p className="text-white text-xl">Loading...</p>
        </div>
      </div>
    );
  }

  if (!eligibility?.eligible && !submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-6">
        <div className="max-w-md text-center bg-white/5 border border-white/10 rounded-2xl p-10">
          <AlertCircle className="w-14 h-14 text-amber-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-3">This link isn't ready for reviews yet</h1>
          <p className="text-gray-400 mb-6">
            {eligibility?.error || "This order doesn't appear to be marked as paid in full yet. If you believe this is a mistake, please reply to the email we sent you."}
          </p>
          <a href="/" className="inline-flex items-center justify-center px-6 py-3 bg-amber-500 text-black font-semibold rounded-full hover:bg-amber-600 transition-colors">
            Back to Visual Creationz
          </a>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-6">
        <div className="max-w-lg text-center bg-white/5 border border-white/10 rounded-2xl p-10" data-testid="review-submitted-card">
          <CheckCircle className="w-20 h-20 text-green-400 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-3">Thank you!</h1>
          <p className="text-gray-300 text-lg mb-6">
            Your review means everything to us. We may feature your kind words on the website to help other families and couples find us.
          </p>
          <a href="/" className="inline-flex items-center justify-center px-6 py-3 bg-amber-500 text-black font-semibold rounded-full hover:bg-amber-600 transition-colors">
            Back to Visual Creationz
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-12 px-4" data-testid="review-page">
      <div className="container mx-auto max-w-2xl">
        <div className="text-center mb-8">
          <Quote className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold mb-3">How did we do?</h1>
          <p className="text-xl text-gray-300">
            We'd love to hear about your experience with Visual Creationz
          </p>
        </div>

        <form onSubmit={submit} className="bg-white/5 border border-white/10 rounded-2xl p-8 space-y-6">
          {/* Star rating */}
          <div className="text-center">
            <label className="block text-sm font-medium text-gray-300 mb-3">Your rating</label>
            <div className="flex justify-center gap-1" data-testid="review-rating">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setForm({ ...form, rating: n })}
                  className="p-1 transition-transform hover:scale-110"
                  aria-label={`${n} stars`}
                  data-testid={`review-star-${n}`}
                >
                  <Star
                    size={42}
                    className={n <= form.rating ? 'text-amber-400' : 'text-gray-600'}
                    fill={n <= form.rating ? 'currentColor' : 'none'}
                  />
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Your name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="review-name-input"
                className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/30 outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Occasion</label>
              <input
                type="text"
                value={form.occasion}
                onChange={(e) => setForm({ ...form, occasion: e.target.value })}
                data-testid="review-occasion-input"
                placeholder="e.g. Wedding, Memorial, 50th Birthday"
                className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/30 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Your review</label>
            <textarea
              value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              rows={5}
              data-testid="review-text-input"
              placeholder="Tell us how we did and what your experience was like..."
              className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/30 outline-none resize-none"
              required
              minLength={10}
            />
            <p className="text-xs text-gray-500 mt-2">
              By submitting you agree we may publish your review on the Visual Creationz website. We'll never share your email or personal details.
            </p>
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="review-submit-btn"
            className="w-full px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold rounded-full hover:from-amber-600 hover:to-amber-700 transition-all disabled:opacity-50 shadow-xl"
          >
            {submitting ? 'Submitting…' : 'Share My Review'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ReviewSubmissionPage;
